/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BusinessNumberType,
  CommunicationRestService,
  CommunicationType,
  CommunityRestService,
  ContactDeliveryMethod,
  ContactDetailsInvitationSubTabDto,
  ContactDisplay,
  ContactPayBy,
  ContactRestService,
  ContactRoleType,
  ContactTermsPayables,
  ContactTermsReceivables,
  ContactType,
  CreditorType,
  EmailMatch,
  LocationRestService,
  SupplierCategoryType,
  WithholdingTaxType,
} from '@gh/builder-shared-data';
import {
  ContextRoute,
  getContextRouteParam,
  InitContextViewService,
} from '@gh/common-internal-shared';
import { DictionaryService, globalDictionaries } from '@gh/common-shared';
import { FilteredDataQuery } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import {
  ContactDuplicatesDialogConfig,
} from '../components/contact-duplicates-dialog/contact-duplicates-dialog.component';
import {
  ContactDuplicateCheck,
  ContactDuplicateFilter,
  ContactDuplicateItem,
} from './model/contact-details-common.model';
import {
  ContactDetails,
  contactDetailsFromDto,
  contactDetailsToDto,
  ContactLocation,
} from './model/contact-details.model';
import { ID } from '@gh/common-shared-data';
import { CommunicationListItem } from '../../../builder-job-admin/jobs';

@Injectable()
export class ContactDetailsBusinessService implements InitContextViewService {

  constructor(private dictionaryService: DictionaryService,
              private contactRest: ContactRestService,
              private communityRest: CommunityRestService,
              private locationDataService: LocationRestService,
              private communicationService: CommunicationRestService,
              private gridLayoutService: GridLayoutService) {

  }

  initContextView(route: ContextRoute): any {
    const contactId = getContextRouteParam(route, 'contactId');
    return {
      contact: isNil(contactId) ? this.getNew() : this.get(contactId),
      contactTypes: this.dictionaryService.getByEnum(ContactType),
      deliveryMethods: this.dictionaryService.getByEnum(ContactDeliveryMethod),
    };
  }

  initCommonTabView(): any {
    return {
      contactTypes: this.dictionaryService.getByEnum(ContactType),
      contactRoleTypes: this.dictionaryService.getByEnum(ContactRoleType),
      contactDisplay: this.dictionaryService.getByEnum(ContactDisplay),
      states: this.dictionaryService.get(globalDictionaries.states),
    };
  }

  initEmployeeCommonTabView(): any {
    return {
      genders: this.dictionaryService.getByEnumGlobal(globalDictionaries.gender),
      payrolls: this.dictionaryService.getByEnumGlobal(globalDictionaries.employeePayroll),
      payBasis: this.dictionaryService.getByEnumGlobal(globalDictionaries.payBasis),
    };
  }

  initCreditorCommonTabView(): any {
    return {
      creditorTypes: this.dictionaryService.getByEnum(CreditorType),
      deliveryMethods: this.dictionaryService.getByEnum(ContactDeliveryMethod),
      emailMatches: this.dictionaryService.getByEnum(EmailMatch),
      supplierCategories: this.dictionaryService.getByEnum(SupplierCategoryType),
      businessNumberTypes: this.dictionaryService.getByEnum(BusinessNumberType),
      whtTaxTypes: this.dictionaryService.getByEnum(WithholdingTaxType),
    };
  }

  initCreditorSubContractorTabView(): any {
    return {};
  }

  initCustomerCommonTabView(): any {
    return {
      deliveryMethods: this.dictionaryService.getByEnum(ContactDeliveryMethod),
      referredBy: this.dictionaryService.get(globalDictionaries.referredBy),
      communities: this.communityRest.findAll({ onlyActive: true, autocomplete: true }),
    };
  }

  initFinanceCompanyCommonTabView(): any {
    return {
      deliveryMethods: this.dictionaryService.getByEnum(ContactDeliveryMethod),
    };
  }

  initDebtorCommonTabView(): any {
    return {
      deliveryMethods: this.dictionaryService.getByEnum(ContactDeliveryMethod),
    };
  }

  initSharedPaymentPayerTabView(): any {
    return {
      payBy: this.dictionaryService.getByEnum(ContactPayBy),
    };
  }

  initSharedPaymentPayeeTabView(): any {
    return {
      paidBy: this.dictionaryService.getByEnumGlobal(globalDictionaries.paidBy),
    };
  }

  initSharedCommunicationsTabView(): any {
    return {
      communicationTypes: this.dictionaryService.getByEnum(CommunicationType),
    };
  }

  initSharedTermsPayerTabView(): any {
    return {
      termsReceivables: this.dictionaryService.getByEnum(ContactTermsReceivables),
    };
  }

  initSharedTermsPayeeTabView(): any {
    return {
      termsPayables: this.dictionaryService.getByEnum(ContactTermsPayables),
    };
  }

  initDuplicatesDialogView(route: DialogRouteSnapshot<ContactDuplicatesDialogConfig>): any {
    return {
      duplicates: this.findDuplicates(route.input.duplicateFilter),
      gridLayout: this.gridLayoutService.get('contactDuplicates'),
    };
  }

  initInvitations(): any {
    return {
      gridLayout: this.gridLayoutService.get('invitations'),
    };
  }

  getNew(): Observable<ContactDetails> {
    return this.contactRest.getNew().pipe(map$(contactDetailsFromDto));
  }

  get(id: string): Observable<ContactDetails> {
    return this.contactRest.get(id).pipe(map$(contactDetailsFromDto));
  }

  checkSyncStatus(id: string): Observable<any> {
    return this.contactRest.checkSyncStatus(id);
  }

  create(details: ContactDetails): Observable<ContactDetails> {
    return this.contactRest.create(contactDetailsToDto(details)).pipe(map$(contactDetailsFromDto));
  }

  update(details: ContactDetails): Observable<ContactDetails> {
    return this.contactRest.update(contactDetailsToDto(details)).pipe(map$(contactDetailsFromDto));
  }

  resendInvite(
    id: string,
    invitation: ContactDetailsInvitationSubTabDto,
    type: ContactType): Observable<ContactDetails> {
    return this.contactRest.resendInviteToPortal(id, invitation, type);
  }

  deleteInvitation(
    id: string,
    invitation: ContactDetailsInvitationSubTabDto,
    type: ContactType): Observable<any> {
    return this.contactRest.deleteInvitation(id, invitation, type);
  }

  inviteToPortal(id: string, email: string, type: ContactType): Observable<ContactDetails> {
    return this.contactRest.inviteToPortal(id, email, type);
  }

  checkDuplicates(filter: ContactDuplicateFilter): Observable<ContactDuplicateCheck> {
    return this.contactRest.checkDuplicates(filter);
  }

  findDuplicates(filter: ContactDuplicateFilter): Observable<ContactDuplicateItem[]> {
    return this.contactRest.findDuplicates(filter);
  }

  findLocations(filter: FilteredDataQuery<string>): Observable<ContactLocation[]> {
    return this.locationDataService.findSlicedByCity(filter);
  }

  syncWithXero(id: string): Observable<any> {
    return this.contactRest.syncWithXero(id);
  }

  getCommunicationDetails(id: ID): Observable<CommunicationListItem> {
    return this.communicationService.get(id);
  }

  readSms(id: ID): Observable<any> {
    return this.communicationService.setSmsAsRead(id);
  }
}
